import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { AgentEditorForm } from "@/components/agents/agent-editor-form";
import {
	DETAIL_CRUMB_LINK_CLASS,
	DetailBreadcrumb,
	DetailHeader,
} from "@/components/connectors/detail-header";
import { TabEmptyState } from "@/components/entity-tabs/tab-states";
import { FormLoadingShell } from "@/components/ui/form-loading-shell";
import { PageColumn, PageShell } from "@/components/ui/page-shell";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { useAgents } from "@/lib/api";
import { useAuthState } from "@/lib/auth-state";
import { parsePrefilledChannel } from "@/lib/automations/prefilled-channel";
import { OWNER_SECTIONS } from "@/lib/owner-sections";

export const Route = createFileRoute("/$owner/agents/new")({
	component: NewAgentPage,
	validateSearch: parsePrefilledChannel,
});

function NewAgentPage() {
	const { owner } = Route.useParams();
	const continuation = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { isAuthenticated } = useAuthState();
	const { isMember, isLoading: isOrgLoading } = useCurrentOrg();
	const canManageWorkspace = isAuthenticated && isMember;
	const { data: agents = [], isLoading: isAgentsLoading } = useAgents({
		enabled: canManageWorkspace,
	});

	return (
		<PageShell className="gap-3 pt-2.5 pb-8">
			<PageColumn className="flex flex-1 flex-col gap-6">
				<DetailHeader
					breadcrumb={
						<DetailBreadcrumb
							crumbs={[
								<Link
									key="agents"
									to="/$owner/agents"
									params={{ owner }}
									className={DETAIL_CRUMB_LINK_CLASS}
								>
									Agents
								</Link>,
							]}
						/>
					}
					icon={<OWNER_SECTIONS.agents.icon className="h-6 w-6" />}
					title="Build a managed agent"
					caption="Create an agent that Lobu runs and manages for this workspace."
				/>
				{isOrgLoading || (canManageWorkspace && isAgentsLoading) ? (
					<FormLoadingShell rows={1} />
				) : !canManageWorkspace ? (
					<TabEmptyState
						icon={Lock}
						title="Workspace membership required"
						description="Only workspace members can build managed agents."
					/>
				) : (
					<AgentEditorForm
						mode={{ kind: "create" }}
						existingAgentIds={agents.map((agent) => agent.agentId)}
						onCreated={async (agentId) => {
							const queryKey = ["agents", owner] as const;
							await queryClient.refetchQueries({ queryKey, exact: true });
							if (continuation.platform && continuation.listen) {
								await navigate({
									to: "/$owner/automations/new",
									params: { owner },
									search: { ...continuation, agent: agentId },
								});
								return;
							}
							await navigate({
								to: "/$owner/agents/$agentId",
								params: { owner, agentId },
							});
						}}
					/>
				)}
			</PageColumn>
		</PageShell>
	);
}
